/**
 * The design settings screen's pure logic (SD-8.1, FR-33, FR-39, UX-DR18).
 *
 * WHY SHIPPED_DEFAULTS IS DUPLICATED HERE from the 20260803 migration: the badge's whole job is
 * to say which numbers the tenant chose and which ones nobody did, and the database cannot
 * answer that — a row holding 18 is indistinguishable from a row where an admin typed 18. The
 * test pins these against the migration's literal defaults so the copy cannot drift.
 *
 * NUMERIC COLUMNS ARRIVE AS STRINGS from PostgREST. Every comparison here goes through `num()`
 * first, because `0.1710 !== '0.1710'` would mark the utility rate as changed the moment the row
 * loads, and a save bar that is dirty on load teaches admins to ignore it.
 *
 * THE STAMP IS FORMATTED FROM THE UTC DATE PART, by regex, not `new Date()` + locale methods: a
 * local-time rendering makes the badge's date depend on the viewer's machine, so two admins see
 * two different dates for one edit. Day-level attribution in UTC is deterministic everywhere and
 * at worst one day off for the editor themselves, which the test can state rather than dodge.
 */

import { savedSetbackMessage } from '@/lib/solarDesignCopy';

export interface DesignSettingsValues {
  setback_ridge_in: number;
  setback_eave_in: number;
  setback_rake_in: number;
  utility_rate_per_kwh: number;
  utility_escalation_pct: number;
  price_per_watt: number;
  default_module_id: string | null;
}

export type DesignSettingsField = keyof DesignSettingsValues;

/** The 20260803 migration's column defaults, verbatim. Pinned by the test. */
export const SHIPPED_DEFAULTS: DesignSettingsValues = Object.freeze({
  setback_ridge_in: 18,
  setback_eave_in: 12,
  setback_rake_in: 12,
  utility_rate_per_kwh: 0.171,
  utility_escalation_pct: 3.5,
  price_per_watt: 3.1,
  default_module_id: null,
});

export const FIELD_LABELS: Record<DesignSettingsField, string> = {
  setback_ridge_in: 'ridge setback',
  setback_eave_in: 'eave setback',
  setback_rake_in: 'rake setback',
  utility_rate_per_kwh: 'utility rate',
  utility_escalation_pct: 'utility escalation',
  price_per_watt: 'price per watt',
  default_module_id: 'default module',
};

const num = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/** Value equality that survives PostgREST's NUMERIC-as-string. */
function sameValue(a: unknown, b: unknown): boolean {
  const na = num(a);
  const nb = num(b);
  if (na !== null || nb !== null) return na === nb;
  return (a ?? null) === (b ?? null);
}

/** The fields whose values differ, in the stable field order. */
export function changedFields(
  before: DesignSettingsValues,
  after: DesignSettingsValues
): DesignSettingsField[] {
  return (Object.keys(SHIPPED_DEFAULTS) as DesignSettingsField[]).filter(
    (field) => !sameValue(before[field], after[field])
  );
}

/**
 * The save confirmation: WHAT changed and that existing designs are untouched (C.5 "Saved").
 *
 * The ridge setback uses the pinned `saved_setback` copy-deck string verbatim — it already
 * carries the untouched clause. Everything else is named in words with the same clause appended,
 * because "Saved" alone is the version that generates the support ticket.
 */
const UNTOUCHED = 'Designs that already exist keep the values they were drawn with.';

export function savedConfirmation(
  changed: DesignSettingsField[],
  saved: DesignSettingsValues
): string {
  const others = changed.filter((f) => f !== 'setback_ridge_in');
  const parts: string[] = [];

  if (changed.includes('setback_ridge_in')) {
    parts.push(savedSetbackMessage(saved.setback_ridge_in));
    if (others.length) {
      parts.push(`Also updated: ${others.map((f) => FIELD_LABELS[f]).join(', ')}.`);
    }
  } else {
    parts.push(`Saved. New designs will use the updated ${others.map((f) => FIELD_LABELS[f]).join(', ')}.`);
    parts.push(UNTOUCHED);
  }
  return parts.join(' ');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** `'2026-03-12T…'` → `'12 Mar'` (UTC date part, fixed English months). */
export function updatedStamp(updatedAt: string | null | undefined): string | null {
  if (typeof updatedAt !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ]/.exec(updatedAt.trim());
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return `${Number(match[3])} ${MONTHS[month - 1]}`;
}

export interface FieldBadge {
  kind: 'default' | 'yours';
  label: string;
}

export interface EditorStamp {
  editorName: string | null;
  updatedAt: string | null;
}

/**
 * UX-DR18: "yours, Allan, 12 Mar" rather than just "yours". The name and date are ROW-level
 * (one stamp trigger, one editor), the differs-from-default decision is per FIELD — which is
 * exactly what the spec's example shows, and honest as long as the label never claims per-field
 * authorship, which "yours, Allan, 12 Mar" does not.
 */
export function fieldBadge(
  field: DesignSettingsField,
  values: DesignSettingsValues,
  stamp: EditorStamp
): FieldBadge {
  if (sameValue(values[field], SHIPPED_DEFAULTS[field])) {
    return { kind: 'default', label: 'Shipped default' };
  }
  const date = updatedStamp(stamp.updatedAt);
  const pieces = ['yours', stamp.editorName, date].filter(Boolean);
  return { kind: 'yours', label: pieces.join(', ') };
}
